import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { ApiError, get, post } from "./api";
import type { Me, Meta } from "./types";

export const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 2_000, retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2, refetchOnWindowFocus: true } },
});

/* ── sesión ── */
export function useMe() {
  return useQuery({ queryKey: ["me"], queryFn: () => get<Me>("/auth/me"), retry: false, staleTime: 60_000 });
}
export function useMeta() {
  return useQuery({ queryKey: ["meta"], queryFn: () => get<Meta>("/meta"), staleTime: 60_000 });
}
export function useLogout() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: () => post("/auth/logout"), onSuccess: () => { qc.clear(); qc.setQueryData(["me"], null); location.assign("/login"); } });
}

/* ── tema ── */
export function useTheme() {
  const [theme, setTheme] = useState<"dark" | "light">(() => {
    try { return (localStorage.getItem("bgf-theme") as "dark" | "light") ?? (matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark"); } catch { return "dark"; }
  });
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try { localStorage.setItem("bgf-theme", theme); } catch { /* almacenamiento no disponible */ }
  }, [theme]);
  return { theme, toggle: () => setTheme((t) => (t === "dark" ? "light" : "dark")) };
}

export function useIsMobile(bp = 1100) {
  const q = `(max-width: ${bp - 1}px)`;
  const [m, setM] = useState(() => matchMedia(q).matches);
  useEffect(() => { const mq = matchMedia(q); const f = () => setM(mq.matches); mq.addEventListener("change", f); return () => mq.removeEventListener("change", f); }, [q]);
  return m;
}

/* ── toasts ── */
interface Toast { id: number; text: string; tone: "info" | "error" | "ok" }
const ToastCtx = createContext<(text: string, tone?: Toast["tone"]) => void>(() => {});
export const useToast = () => useContext(ToastCtx);
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const push = useCallback((text: string, tone: Toast["tone"] = "info") => {
    const id = Date.now() + Math.random();
    setItems((s) => [...s.slice(-2), { id, text, tone }]);
    setTimeout(() => setItems((s) => s.filter((t) => t.id !== id)), tone === "error" ? 6000 : 3600);
  }, []);
  const value = useMemo(() => push, [push]);
  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-24 z-50 flex flex-col items-center gap-2 px-4 md:bottom-6" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={`slide-up pointer-events-auto max-w-[min(560px,100%)] rounded-card border bg-rs px-4 py-3 text-[14px] leading-snug shadow-lg ${t.tone === "error" ? "border-er text-tx" : t.tone === "ok" ? "border-gd text-tx" : "border-ln2 text-tx"}`}>{t.text}</div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

/** Mutación con toast de error/éxito e invalidación de las consultas afectadas. */
export function useAction<TVars = void, TRes = unknown>(fn: (v: TVars) => Promise<TRes>, o: { ok?: string | ((r: TRes) => string); invalidate?: string[][]; onDone?: (r: TRes) => void } = {}) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r) => {
      const msg = typeof o.ok === "function" ? o.ok(r) : o.ok;
      if (msg) toast(msg, "ok");
      for (const k of o.invalidate ?? [["missions"], ["mission"], ["overview"], ["notifications"]]) void qc.invalidateQueries({ queryKey: k });
      o.onDone?.(r);
    },
    onError: (e) => toast(e instanceof Error ? e.message : "Error inesperado", "error"),
  });
}
